import {BUBBLE, Component, DONE, isCopyModifier} from "@/event/calendar/dom/Component.ts";
import {DrawingContext} from "@/event/calendar/dom/DrawingContext.ts";
import {
	CANCELED_OPACITY,
	Coordinates,
	decodeNumericIconEntity,
	DONE_OPACITY,
	DONE_SATURATION,
	drawHoliday,
	intersection,
	intersectRanges,
	MOVE_OPACITY,
	parseHexColor,
	Range,
	Rect,
	reduceSaturation,
	rgbToHex,
	SnapCoordinates
} from "@/event/calendar/Shapes.ts";
import {DayColumn, DayColumns, HasDayColumns, weekGridDays} from "@/event/calendar/days/DaysComponent.ts";
import {drawVerticalLines} from "@/event/calendar/days/DaysTopHeader.ts";
import {CalendarView, DISPLAY_CUSTOMER, DISPLAY_DESCRIPTION, DISPLAY_PLACE, DISPLAY_SUBJECT} from "@/event/calendar/CalendarModel.ts";
import {calculateEventLayers, CalEvent, DateRange, DUMMY_ID, EventRect, overlaps, normalizeRange, timeFormat, timeRange, uidToPreviewKey} from "@/store/CalendarStore.ts";
import {calcTimeGrid, calcVertPosition, drawPlusBullet, MouseAction, MouseStartAction, PLUS_RADIUS, priorityBorderColor, setNewDateAfterScrolling, strokeCanceled} from "@/event/calendar/days/DaysTimedBody.ts";
import {COLOR_NAMES, DEFAULT_EVENT_COLOR} from "@t/fd/editor/EventColor.ts";
import {newShifted, sameDay, shiftDate} from "@t/util/DateUtil.ts";
import {drawTextLine, TextRun} from "@/bpm/canvas/CanvasUtils.ts";
import {getDataTransfer, hasIdentity, ParsedType, parseObjectRef, setDataTransfer, uidToRef} from "@/utils/DragDropUtils.ts";
import {CanvasRootComponent} from "@/event/calendar/dom/CanvasRootComponent.ts";
import {ICON_FONT} from "@/event/calendar/Constants.ts";
import {Icon} from "anneca-symbols";
import {MIN_SNAP_DISTANCE} from "@/event/calendar/dom/ScrollComponent.ts";
import {compareAllDay, compareComplete, comparePriority} from "@/event/calendar/weeks/WeeksBodyComponent.ts";

export const ALL_DAY_EVENT_HEIGHT = 20;
const DAY_MILLIS = 24 * 3600000;

export class DaysWholeBody extends Component implements HasDayColumns {
	public dayColumns: DayColumns;
	private view: CalendarView;
	private eventShapes:EventRect[];
	public eventStackHeight:number;

	/** Range of drop cursor */
	private dropFrom:Date|null;
	private dropTo:Date|null;

	/* Mouse actions */
	private startX:number;
	private startY:number;
	private startDay: Date;	// The time where users started to manipulate event
	private startEventRange: DateRange;	// Original event time span

	private action: MouseAction;
	private actionObject:EventRect;

	/** Create new placeholder */
	private createDummy: CalEvent;

	constructor(view:CalendarView) {
		super();
		this.view = view;
		this.action = "idle";
		this.createDummy = null;
		this.dropFrom = this.dropTo = null;
		this.actionObject = null;
		this.eventStackHeight = 0;	// updated on every redraw
	}

	scrollIdle() {
		setNewDateAfterScrolling(this.view, this.dayColumns);
	}

	/**
	 * Calculate vertical space required to fit all current events
	 */
	stackedEventsHeight() {

	}

	verticalScrollRange(): Range {
		return {
			min: 0,
			max: Math.max(this.eventStackHeight - this.height, 0)
		};
	}

	protected drawBackground(dc: DrawingContext, area: Rect) {
		dc.ctx.fillStyle = dc.style.getPropertyValue("--cal-bg");
		dc.ctx.fillRect(area.x, area.y, area.width, area.height);

		// Fill weekend backgrounds
		const weekendBodyColor = dc.style.getPropertyValue("--cal-weekend-bg");
		const todayBodyColor = dc.style.getPropertyValue("--cal-today-body");
		const holidayColor = dc.style.getPropertyValue("--cal-today-bg");
		const fontFamily = dc.style.getPropertyValue("--font");

		for (let col of this.dayColumns.days) {
			const day = col.date.getDay();
			if (col.today) {
				dc.ctx.fillStyle = todayBodyColor;
				let dayRect = { x: col.x, y: -10000, width: col.width, height: 20000 };
				dayRect = intersection(dayRect, area);
				dc.ctx.fillRect(dayRect.x, dayRect.y, dayRect.width, dayRect.height);
			} else if (day === 0 || day === 6) { /* sunday, saturday */
				dc.ctx.fillStyle = weekendBodyColor;
				let dayRect = { x: col.x, y: 0, width: col.width, height: this.height };
				dayRect = intersection(dayRect, area);
				dc.ctx.fillRect(dayRect.x, dayRect.y, dayRect.width, dayRect.height);
			}
			const h = this.view.store.getHoliday(col.date);
			if (h) drawHoliday(dc.ctx, h, { x: col.x, y: area.y, width: col.width, height: area.height}, fontFamily, holidayColor);
		}
	}

	protected drawBorder(dc: DrawingContext, area: Rect) {
		dc.ctx.strokeStyle = dc.style.getPropertyValue("--cal-day-separ");
		drawVerticalLines(dc.ctx, this.dayColumns, area);
	}

	/**
	 * Find view x coordinate for given day
	 * @param day	day to find x coordinate of
	 * @param start this represents start of the event
	 */
	private xPosOf(day: Date, start = true):number {

		// If the day denotes end of interval it is basically next day at 00:00 .. we shift to propper day
		if (!start) {
			day = newShifted(day, -1);
		}

		const startCutoff = this.dayColumns.first.getTime() - 12 * 3600000;	// first/last is at noon
		const endCutoff = this.dayColumns.last.getTime() + 12 * 3600000;	// first/last is at noon
		//console.log("Day="+day+"   cutoff="+new Date(startCutoff)+" ... "+new Date(endCutoff));

		const dayStart = new Date(day.getTime());
		dayStart.setHours(0, 0, 0, 0);
		const dayEnd = newShifted(dayStart);
		//console.log("Day start ="+dayStart+"   Day end="+new Date(dayEnd));

		if (this.dayColumns.days.length > 0) {
			if (dayStart.getTime() < startCutoff) {
				// the day is out of view to the left (will be clipped)
				//console.log("..start cutoff");
				return this.dayColumns.days[0].x - 100;
			} else if (dayEnd.getTime() > endCutoff) {
				//console.log("..end cutoff");
				const lastCol = this.dayColumns.days[this.dayColumns.days.length - 1];
				return lastCol.x + lastCol.width + 100;
			}
			// Now we know the day is inside the columns
			for (let col of this.dayColumns.days) {
				if (sameDay(col.date, day)) {
					return start ? col.x : (col.x + col.width);
				}
			}
		}
		return 0;
	}

	private sortForDisplay(events:CalEvent[]):CalEvent[] {
		const copy:CalEvent[] = [ ...events ];
		copy.sort((e1:CalEvent, e2:CalEvent) => {
			let val = compareComplete(e1, e2);
			if (val === 0) {
				val = e1.start.getTime() - e2.start.getTime();
				if (val === 0) {
					val = comparePriority(e1, e2);
				}
			}
			return val;
		});
		return copy;
	}

	protected drawContent(dc: DrawingContext, area: Rect) {
		const r = normalizeRange(this.dayColumns.first, this.dayColumns.last);
		const allDayEvents:CalEvent[] = this.sortForDisplay(this.view.store.getAllDayEvents(r.start, r.end));
		if (this.createDummy && overlaps(r, { start: this.createDummy.start, end: this.createDummy.end })) allDayEvents.push(this.createDummy); //allDayEvents.splice(0, 0, this.createDummy);

		const layers: CalEvent[][] = calculateEventLayers(allDayEvents);
		const eventShapes:EventRect[] = [];

		for (let l = 0; l < layers.length; l++) {
			const layer = layers[l];
			for (let e of layer) {
				const y = l * (ALL_DAY_EVENT_HEIGHT+1) + 1;
				const startX = this.xPosOf(e.start);
				const endX = this.xPosOf(e.end, false);
				let erect = drawEventRow(dc, e, startX, y, endX - startX, ALL_DAY_EVENT_HEIGHT, false, area, this.view);
				if (e.uid !== DUMMY_ID) eventShapes.push(erect);
			}
		}
		this.eventShapes = eventShapes;
		this.eventStackHeight = layers.length * (ALL_DAY_EVENT_HEIGHT+1) + 1;

		/** Optionally draw drop cursor */
		if (this.dropFrom && this.dropTo) {
			const isCopy = ("copy" === this.cursor);

			const startX = this.xPosOf(this.dropFrom);
			const endX = this.xPosOf(this.dropTo, false);
			const selBg = dc.style.getPropertyValue("--selection-bg");
			dc.ctx.strokeStyle = selBg;
			if (selBg.startsWith("#")) dc.ctx.fillStyle = selBg + "55";	// add opacity to hex color
			else dc.ctx.fillStyle = selBg;
			const ypos = 1;

			dc.ctx.lineWidth = 2;
			dc.ctx.beginPath();
			dc.ctx.roundRect(startX + 1, ypos + 1, (endX - startX) - 2, ALL_DAY_EVENT_HEIGHT - 2, (ALL_DAY_EVENT_HEIGHT - 2)/2);
			dc.ctx.fill();
			dc.ctx.stroke();
			if (isCopy) {
				const cx = startX + 4.5 + PLUS_RADIUS;
				const cy = ypos + 0.5 + PLUS_RADIUS + ALL_DAY_EVENT_HEIGHT/2;
				drawPlusBullet(dc, cx, cy);
			}
		}
	}

	getSnapPosition(x: number, y: number, xPositiveDir?:boolean, yPositiveDir?:boolean): SnapCoordinates | null {
		const width = this.width / weekGridDays(this.view)

		const lowerSnapX = Math.floor(x / width) * width;
		const higherSnapX = Math.ceil(x / width) * width;
		const lowerDistance = Math.abs(x - lowerSnapX);
		const higherDistance = Math.abs(x - higherSnapX);
		let snapX;
		if (lowerDistance < MIN_SNAP_DISTANCE && higherDistance >= MIN_SNAP_DISTANCE) snapX = lowerSnapX;
		else if (higherDistance < MIN_SNAP_DISTANCE && lowerDistance >= MIN_SNAP_DISTANCE) snapX = higherSnapX;
		else snapX = xPositiveDir ? higherSnapX : lowerSnapX;

		if (x === snapX) return null;
		return { x: snapX, y: 0, snapX:true, snapY: false };
	}

	determineStartAction(e: PointerEvent, x: number, y: number):{ action: MouseStartAction, er?:EventRect } {
		const ctx = this.root().ctx;
		for (let er of this.eventShapes) {
			if (er?.startDragShape && ctx.isPointInPath(er.startDragShape, x, y)) {
				return { action: er.event.movable ? "start":"none", er: er };
			}
			if (er?.endDragShape && ctx.isPointInPath(er.endDragShape, x, y)) {
				return { action: er.event.movable ? "end":"none", er: er };
			}
			if (er.shape && ctx.isPointInPath(er.shape, x, y)) {
				return { action: "event", er: er };
			}
		}
		return { action: "bg" };
	}


	cursorToDay(x: number, y: number):Date {
		for (let d of this.dayColumns.days) {
			if (x >= d.x && x < (d.x + d.width)) {
				return new Date(d.date.getTime());
			}
		}
		return null;
	}


	onPointerDown(e: PointerEvent, x: number, y: number): boolean {
		const ctx = this.root().ctx;
		this.startX = x;
		this.startY = y;

		this.startDay = this.cursorToDay(x, y);
		const { action, er } = this.determineStartAction(e, x, y);
		this.action = action;
		this.actionObject = er;

		if (e.pointerType === "mouse") {
			switch (action) {
				case "start":
					this.startEventRange = {start: er.event.start, end: er.event.end};
					e.preventDefault();
					this.setPointerCapture(e);
					return DONE;
				case "end":
					this.startEventRange = {start: er.event.start, end: er.event.end};
					e.preventDefault();
					this.setPointerCapture(e);
					return DONE;
				case "event":
					if (er.event.movable) {
						this.root().canvas.setAttribute("draggable", "true");
						this.startEventRange = {start: er.event.start, end: er.event.end};
						return DONE;
					}
					break;
				case "bg":
					e.preventDefault();
					this.setPointerCapture(e);
					return DONE;
			}
			return BUBBLE;
		} else {
			// Touch & Pen devices
			switch (action) {
				case "start":
				case "end":
				case "event":
					this.action = "event";	// ignore start/end
					if (er.event.movable) {
						this.root().canvas.setAttribute("draggable", "true");
						this.startEventRange = { start: er.event.start, end: er.event.end };
						return DONE;
					}
					break;
				case "bg":
					this.action = "event";
					this.root().canvas.setAttribute("draggable", "true");
					const start = new Date(this.startDay);
					start.setHours(0, 0, 0, 0);
					const end = newShifted(start, 1);
					this.actionObject = this.buildEventRect(this.dummyEvent(start, end), y);
					this.startEventRange = { start, end };
					return DONE;
				default:
					this.action = "idle";
					this.actionObject = null;
			}
			return BUBBLE;

		}
	}

	dummyEvent(start:Date, end:Date):CalEvent {
		return { uid: DUMMY_ID,
			title: this.root().env.msg("event.create.dummy"),
			color: (this.view.defaultColor ?? DEFAULT_EVENT_COLOR),
			start, end };
	}

	buildEventRect(e:CalEvent, y:number):EventRect {
		let col:DayColumn;
		for (let d of this.dayColumns.days) {
			if (sameDay(d.date, e.start)) {
				col = d;
				break;
			}
		}

		const eventX = col.x+1;
		const eventWidth = col.width - 2;
		const eventY = y - ALL_DAY_EVENT_HEIGHT / 2;
		const eventHeight = ALL_DAY_EVENT_HEIGHT;

		const shape = new Path2D();
		shape.roundRect(eventX, eventY, eventWidth, eventHeight, ALL_DAY_EVENT_HEIGHT/2);

		const er:EventRect = {
			x:eventX, y: eventY, width: eventWidth, height: eventHeight,
			event:e,
			shape
		};
		return er;
	}

	onPointerMove(e: PointerEvent, x: number, y: number): boolean {
		if (e.pointerType === "mouse") {
			this.cursor = null;
			if (this.action === "idle") {
				const { action, er} = this.determineStartAction(e, x, y);
				if (action === "event") this.cursorInPreviewObject(er, uidToPreviewKey(er.event.uid));
				else this.cursorOutOfPreviewObject();
				switch (action) {
					case "start":
						this.cursor = "ew-resize";
						break;
					case "end":
						this.cursor = "ew-resize";
						break;
					case "event":
						this.cursor = "pointer";
						break;
					default:
						this.cursor = "default";
						break;
				}
			} else if (this.action === "bg" || this.action === "start" || this.action === "end") {
				// Waiting to move to start action and keeping the cursor
				const reallyStart:boolean = (Math.max(Math.abs(this.startX - x), Math.abs(this.startY - y))) > 2;
				if (reallyStart) {
					// Mouse moved enough now we know
					switch (this.action) {
						case "bg":
							this.action = "create";
							this.cursor = "default";

							let dummyStart = new Date(this.startDay),
								dummyEnd = this.cursorToDay(x, y);
							if (dummyStart.getTime() > dummyEnd.getTime()) {
								const temp = dummyStart;
								dummyStart = dummyEnd;
								dummyEnd = temp;
							}
							dummyStart.setHours(0, 0, 0, 0);
							dummyEnd.setHours(0, 0, 0, 0);
							shiftDate(dummyEnd);

							const root = this.root();
							this.createDummy = this.dummyEvent(dummyStart, dummyEnd);
							this.dirty(false);
							break;
						case "start":
							this.action = "resize-start";
							this.cursor = "ew-resize";
							this.view.store.manipulationStart(this.actionObject.event.uid);
							break;
						case "end":
							this.action = "resize-end";
							this.cursor = "ew-resize";
							this.view.store.manipulationStart(this.actionObject.event.uid);
							break;
					}
				}
			} else {
				let time;
				if (this.actionObject) {
					switch (this.action) {
						case "resize-start":
							time = this.cursorToDay(x, y);
							if (time) {
								time.setHours(0, 0, 0, 0);
								if (time.getTime() < this.actionObject.event.end.getTime())	this.actionObject.event.start = time;
							}
							this.cursor = "ew-resize";
							break;
						case "resize-end":
							time = this.cursorToDay(x, y);
							if (time) {
								time.setHours(0, 0, 0, 0);
								shiftDate(time);
								if (time.getTime() > this.actionObject.event.start.getTime()) this.actionObject.event.end = time;
							}
							this.cursor = "ew-resize";
							break;
					}
					this.dirty(false);
				} else {
					if (this.action === "create") {

						let dummyStart = new Date(this.startDay),
							dummyEnd = this.cursorToDay(x, y);
						if (dummyEnd) {
							if (dummyStart.getTime() > dummyEnd.getTime()) {
								const temp = dummyStart;
								dummyStart = dummyEnd;
								dummyEnd = temp;
							}
							dummyStart.setHours(0, 0, 0, 0);
							dummyEnd.setHours(0, 0, 0, 0);
							shiftDate(dummyEnd);
							this.createDummy.start = dummyStart;
							this.createDummy.end = dummyEnd;
							this.cursor = "ew-resize";
							this.dirty(false);
						}
					}
				}
			}
		}
		return BUBBLE;
	}

	onPointerUp(e: PointerEvent, x: number, y: number): boolean {
		if (e.pointerType === "mouse") {
			switch (this.action) {
				case "idle":	// nothing happens
					break;
				case "bg":	// candidates for future action
					this.view.store.manipulationEnd();
					let dummyStart = new Date(this.startDay),
						dummyEnd = this.cursorToDay(x, y);
					if (dummyStart.getTime() > dummyEnd.getTime()) {
						const temp = dummyStart;
						dummyStart = dummyEnd;
						dummyEnd = temp;
					}
					dummyStart.setHours(0, 0, 0, 0);
					dummyEnd.setHours(0, 0, 0, 0);
					shiftDate(dummyEnd);
					this.view.store.invokeCreate(true, dummyStart, dummyEnd);
					break;
				case "start":
				case "end":
					this.view.store.manipulationEnd();
					break;
				case "create":
					this.view.store.invokeCreate(true, this.createDummy.start, this.createDummy.end);
					this.createDummy = null;
					break;
				case "resize-start":
					const newStart = this.actionObject.event.start;
					this.actionObject.event.start = this.startEventRange.start;
					this.view.store.update(this.actionObject.event.uid).setStart(newStart).commit();
					this.view.store.manipulationEnd();
					break;
				case "resize-end":
					const newEnd = this.actionObject.event.end;
					this.actionObject.event.end = this.startEventRange.end;
					this.view.store.update(this.actionObject.event.uid).setEnd(newEnd).commit();
					this.view.store.manipulationEnd();
					break;
				case "event":
					this.view.store.invokeEdit(this.actionObject.event.uid);
					break;
			}
			this.action = "idle";
			this.actionObject = null;
			return true;
		} else {
			// Touch & Pen devices
			if (this.action === "event") {
				// may be existing or new
				if (this.actionObject.event.uid === DUMMY_ID) {
					this.view.store.invokeCreate(true, this.actionObject.event.start, this.actionObject.event.end);
				} else {
					this.view.store.invokeEdit(this.actionObject.event.uid);
				}
			}
			this.action = "idle";
			this.actionObject = null;
			return true;
		}
	}


	onDragStart(e: DragEvent, x: number, y: number): boolean {
		super.onDragStart(e, x, y);
		// Set custom drag image
		const root = this.root();
		const dragImage = createEventDragImage(this.actionObject, root); // Function to create your custom drag image
		const offsetX = Math.floor(this.startX - this.actionObject.x);
		const offsetY = Math.floor(this.startY - this.actionObject.y);
		e.dataTransfer.setDragImage(dragImage, offsetX, offsetY);

		// Calculate dayShift
		const columnWidth = this.dayColumns.days[0].width;	// width of the first column
		const dayShift = -1 * Math.floor(offsetX / columnWidth);
		const eventLength = Math.round((this.actionObject.event.end.getTime() - this.actionObject.event.start.getTime()) / DAY_MILLIS);

		// Allow for both copy and move
		e.dataTransfer.effectAllowed = 'copyMove';
		setDataTransfer(e.dataTransfer, { ox:offsetX, oy:offsetY, ds: dayShift, len: eventLength }, uidToRef(this.actionObject.event.uid));

		this.actionObject.event.state = "move";
		this.view.store.manipulationStart(this.actionObject.event.uid);
		this.dirty(false);
		return false;
	}

	onDragEnd(e: DragEvent, x: number, y: number): boolean {
		this.root().canvas.removeAttribute('draggable');
		this.actionObject.event.state = "normal";
		this.dropFrom = this.dropTo = null;
		this.view.store.manipulationEnd();
		this.dirty(false);
		return true;
	}



	onDragOver(e: DragEvent, x: number, y: number): boolean {
		// Find data type we can accept
		let pt:ParsedType = getDataTransfer(e.dataTransfer);
		if (pt) {
			let dayShift = typeof pt.params?.ds === "string" ? parseInt(pt.params.ds) : 0;
			let eventLength = typeof pt.params?.len === "string" ? parseInt(pt.params.len) : 1;

			const eventStart = this.cursorToDay(x, y);
			if (eventStart) {
				shiftDate(eventStart, dayShift);
				eventStart.setHours(0, 0, 0, 0);
				const eventEnd = newShifted(eventStart, eventLength);

				e.preventDefault();	// accepting drop
				// Detect modifier key (Ctrl typically means copy)
				if (isCopyModifier(e)) {
					e.dataTransfer.dropEffect = 'copy';
					this.setCursorForDragEffect("copy");
				} else {
					e.dataTransfer.dropEffect = 'move';
					this.setCursorForDragEffect("move");
				}

				const change = (this.dropFrom == null || this.dropFrom.getTime() !== eventStart.getTime()) || (this.dropTo == null || this.dropTo.getTime() !== eventEnd.getTime());
				if (change) {
					this.dropFrom = eventStart;
					this.dropTo = eventEnd;
					this.dirty(false);
				}
			}
		}
		return true;
	}

	onDragEnter(e: DragEvent, x: number, y: number): boolean {
		return this.onDragOver(e, x, y);
	}

	onDragLeave(e: DragEvent, x: number, y: number): boolean {
		const change = this.dropFrom != null || this.dropTo != null;
		this.dropFrom = null;
		this.dropTo = null;
		if (change) this.dirty(false);
		return true;
	}

	onDrop(e: DragEvent, x: number, y: number): boolean {
		this.dropFrom = this.dropTo = null;	// remove marker
		let pt:ParsedType = getDataTransfer(e.dataTransfer);
		if (pt) {
			let dayShift = typeof pt.params?.ds === "string" ? parseInt(pt.params.ds) : 0;
			let eventLength = typeof pt.params?.len === "string" ? parseInt(pt.params.len) : 1;

			const eventStart = this.cursorToDay(x, y);
			if (eventStart) {
				shiftDate(eventStart, dayShift);
				eventStart.setHours(0, 0, 0, 0);
				e.preventDefault();	// accepting drop
				const effect = e.dataTransfer.dropEffect; // TODO 'copy' or 'move'

				let oref = parseObjectRef(e.dataTransfer.getData(pt.full));
				if (oref) {
					if (hasIdentity(oref)) {
						const isCopy = this.cursor === "copy";
						if (isCopy) this.view.store.invokeCopy(true, eventStart, oref);
						else this.view.store.update(oref).makeAllDay().move(eventStart).commit();
					} else {
						const dend = newShifted(eventStart, 1);
						this.view.store.invokeCreate(true, eventStart, dend, oref);
					}
				}
			}
		}
		this.dirty(false);
		return true;
	}

}

/**
 * Display event in one row. Suitable for all day events
 */
export function drawEventRow(dc:DrawingContext, event:CalEvent, x:number, y:number, width:number, height:number, shadow=false, area?: Rect, view?:CalendarView):EventRect {
	const ff = dc.style.getPropertyValue("--font");
	let fg = dc.style.getPropertyValue("--cal-fg");
	try {
		dc.ctx.save();
		const dummy = DUMMY_ID === event.uid;

		let opacity:number = 1;
		if (event.state === "move") opacity = MOVE_OPACITY;
		if (event.canceled) opacity *= CANCELED_OPACITY;
		else if (event.completed) opacity *= DONE_OPACITY;
		if (opacity < 1) {
			dc.ctx.globalAlpha = opacity;
		}

		let c = event.color;
		if (typeof c !== "number" || c < 0 || c >= COLOR_NAMES.length) c = 3;	// default color
		const colorName = COLOR_NAMES[c];

		let boxColor = dc.style.getPropertyValue("--" + colorName + "-bg");
		if (event.completed || event.canceled) {
			try {
				let rgb = parseHexColor(boxColor);
				rgb = reduceSaturation(rgb.r, rgb.g, rgb.b, DONE_SATURATION);
				boxColor = rgbToHex(rgb);
			} catch(e) {
				console.log("Color name was " + colorName + " and c=" + c);
			}
		}
		dc.ctx.fillStyle = boxColor;

		const er:Partial<EventRect> = { event, x, y, width, height };
		er.shape = new Path2D();
		er.shape.roundRect(x, y, width, height, height/2);
		if (shadow) {
			dc.ctx.shadowColor = "#00000022";
			dc.ctx.shadowBlur = 8;
			dc.ctx.shadowOffsetX = 1;
			dc.ctx.shadowOffsetY = 1;
		}
		dc.ctx.fill(er.shape);

		// Border
		dc.ctx.strokeStyle = priorityBorderColor(event.priority, fg);
		dc.ctx.beginPath();
		dc.ctx.roundRect(x + 0.5, y + 0.5, width - 1, height - 1, 4.5);
		dc.ctx.stroke(er.shape);

		if (shadow) {
			dc.ctx.shadowColor = 'transparent';
			dc.ctx.shadowBlur = 0;
		}
		if (dummy) {
			// Create 2px border around dummy event
			dc.ctx.strokeStyle = fg = dc.style.getPropertyValue("--selection-bg");
			dc.ctx.lineWidth = 2;
			dc.ctx.beginPath();
			dc.ctx.roundRect(x + 1, y + 1, width - 2, height - 2, (height - 2)/2);
			dc.ctx.stroke();
		}
		dc.ctx.clip(er.shape);

		if (event.movable) {
			er.startDragShape = new Path2D();
			er.startDragShape.rect(x, y, 8, height);
			er.endDragShape = new Path2D();
			er.endDragShape.rect(x + width - 8, y, 8, height);
		}

		const PADX = 8;
		let tx = x + PADX;
		let twidth = width - 2 * PADX;

		if (area) {
			const myXstart = tx;
			const myXend = tx + twidth;
			const areaXstart = area.x + 2;
			const areaXend = area.x + area.width - 4;
			const range = intersectRanges(myXstart, myXend, areaXstart, areaXend);
			if (range == null) return er as EventRect;
			tx = range.min;
			twidth = range.max - range.min;
		}
		if (twidth < 16) return er as EventRect;	// too little space to draw anything

		let fontSize = 12;
		if (width < 120) fontSize = width / 10;
		if (fontSize < 8) fontSize = 8;
		const lh = Math.floor(fontSize * 1.5);
		fontSize = Math.floor(fontSize);
		const iconSize = Math.round(14 * fontSize / 12);

		let showTime = true;
		let display = DISPLAY_SUBJECT+DISPLAY_PLACE;
		let showAttendees: "no" | "mono" | "full" = "no";
		if (view) {
			showTime = view.showTime;
			display = view.display;
			showAttendees = view.showAttendees;
		}


		const runs:TextRun[] = [];
		const separ:TextRun = { text: " ~ ", font: fontSize+"px "+ff, color: fg, opacity: 0.5 };

		let first = true;
		for (let i = 0; i < display.length; i++) {
			switch (display.charAt(i)) {
				case DISPLAY_SUBJECT:
					if (first) first = false;
					else runs.push(separ);
					let title = event.title;
					if (event.icon) {
						const eicon = decodeNumericIconEntity(Icon.parse(event.icon).entity);
						runs.push({ text: eicon, font: iconSize+"px "+ICON_FONT, color: fg });
						title = " "+title;	// add space to separate from the icon
					}
					runs.push({ text: title, font: "500 "+fontSize+"px "+ff, color: fg });
					break;
				case DISPLAY_PLACE:
					if (event.place) {
						if (first) first = false;
						else runs.push(separ);
						runs.push({ text: event.place, font: fontSize+"px "+ff, color: fg, opacity: 0.3 });
					}
					break;
				case DISPLAY_CUSTOMER:
					if (event.partyName) {
						if (first) first = false;
						else runs.push(separ);
						runs.push({ text: event.partyName, font: fontSize+"px "+ff, color: fg, opacity: 0.75 });
					}
					break;
				case DISPLAY_DESCRIPTION:
					if (event.comment) {
						if (first) first = false;
						else runs.push(separ);
						runs.push({ text: event.comment, font: fontSize+"px "+ff, color: fg, opacity: 0.5 });
					}
					break;
			}
		}
		if (showAttendees !== "no" && Array.isArray(event.attendees) && event.attendees.length > 0) {
			if (!first) runs.push(separ);
			const names:string[] = [];
			event.attendees.forEach((a) => {
				names.push(a.name);
			});
			runs.push({ text: names.join(", "), font: fontSize+"px "+ff, color: fg, opacity: 0.3 });
		}

		drawTextLine(dc.ctx, runs, tx, y+15, twidth);

		strokeCanceled(event, dc, x, y, width, height);

		return er as EventRect;
	} finally {
		dc.ctx.restore();
	}
}



function createEventDragImage(er: EventRect, root: CanvasRootComponent) {
	const canvas:HTMLCanvasElement = document.createElement('canvas');
	canvas.style.width = er.width + "px";
	canvas.style.height = er.height + "px";
	canvas.style.position = 'absolute';
	canvas.style.top = "-9000px";
	const ctx = canvas.getContext('2d');
	// Set canvas size
	canvas.width = root.ratio *  er.width;
	canvas.height = root.ratio *  er.height;
	ctx.scale(root.ratio, root.ratio);

	drawEventRow({ ctx: ctx, style: root.style, env: root.env, props:{} }, er.event, 0, 0, er.width, er.height, false);

	document.body.appendChild(canvas);
	setTimeout(() => canvas.remove(), 0);
	return canvas;
}